"use client"

import { useMemo } from "react"

export interface TeamLogoFallbackProps {
  teamName: string
  primaryColor?: string
  secondaryColor?: string
  className?: string
}

export function TeamLogoFallback({
  teamName,
  primaryColor = "#333333",
  secondaryColor = "#FFFFFF",
  className = "",
}: TeamLogoFallbackProps) {
  // Generate initials from team name (up to 2 characters)
  const initials = useMemo(() => {
    if (!teamName) return "T"
    
    const words = teamName.trim().split(/\s+/)
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase()
    } else {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase()
    }
  }, [teamName])
  
  // Generate a darker version of primaryColor for stroke
  const strokeColor = useMemo(() => {
    // Convert hex to RGB
    const hex = primaryColor.replace('#', '')
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    
    // Darken by multiplying by 0.7
    const darkenFactor = 0.7
    const newR = Math.floor(r * darkenFactor)
    const newG = Math.floor(g * darkenFactor)
    const newB = Math.floor(b * darkenFactor)
    
    // Convert back to hex
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
  }, [primaryColor])
  
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Background circle */}
      <circle cx="50" cy="50" r="45" fill={primaryColor} stroke={strokeColor} strokeWidth="2" />
      
      {/* Team initials */}
      <text
        x="50"
        y="50"
        dominantBaseline="middle"
        textAnchor="middle"
        fill={secondaryColor}
        fontSize="38"
        fontWeight="bold"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        {initials}
      </text>
    </svg>
  )
}
